import React, { FC, useMemo, useState } from 'react';

import { IImageProps } from './interfaces';
import Lazy from '../../pages/Root/components/Lazy/Lazy.tsx';
import ImageSkeleton from '../../moment/components/ImageSkeleton/ImageSkeleton.tsx';

import './index.scss';

const Image: FC<IImageProps> = ({
  src,
  alt,
  className,
  imageContainerClassName,
  style,
  imageList,
  setRef,
  withSkeleton,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageRef, setImageRef] = useState<HTMLImageElement>();

  const onLoad = () => {
    setIsLoaded(true);
  };

  const classList = useMemo(() => {
    const list = ['image'];

    if (isLoaded) {
      list.push('image--visible');
    }

    if (className) {
      list.push(className);
    }

    return list.join(' ');
  }, [isLoaded, className]);

  const source = useMemo(() => {
    if (src && src !== '') {
      return src;
    }

    if (imageList?.length && imageRef) {
      const { width, height } = imageRef.getBoundingClientRect();
      const containerProportion = width / height;
      
      // SPECIAL CASE: For landscape containers (like 390x240), prioritize 9:4 images
      if (containerProportion > 1.30) { // Landscape container
        const nineByFourImages = imageList.filter(item => {
          const aspect = item.width / item.height;
          return Math.abs(aspect - 2.25) < 0.15; // Allow some tolerance for 9:4
        });
        
        if (nineByFourImages.length > 0) {
          // Sort 9:4 images by size, prioritizing larger than container
          const sortedNineByFour = nineByFourImages.sort((a, b) => {
            // First priority: images larger than container width
            const aIsLarger = a.width >= width;
            const bIsLarger = b.width >= width;
            
            if (aIsLarger && !bIsLarger) return -1;
            if (!aIsLarger && bIsLarger) return 1;
            
            // If both are larger or both are smaller, prefer the larger one
            if (aIsLarger && bIsLarger) {
              return b.width - a.width; // Descending order (largest first)
            } else {
              return b.width - a.width; // Descending order (largest first)
            }
          });
          
          return sortedNineByFour[0]?.url || sortedNineByFour[0]?.link;
        }
      }
      
      // SPECIAL CASE: For square containers (like 118x118), prioritize 1:1 images
      if (Math.abs(containerProportion - 1.0) < 0.1) { // Square container
        const oneByOneImages = imageList.filter(item => {
          const aspect = item.width / item.height;
          return Math.abs(aspect - 1.0) < 0.15; // Allow some tolerance for 1:1
        });
        
        if (oneByOneImages.length > 0) {
          // Sort 1:1 images by size, prioritizing larger than container
          const sortedOneByOne = oneByOneImages.sort((a, b) => {
            // First priority: images larger than container width
            const aIsLarger = a.width >= width;
            const bIsLarger = b.width >= width;
            
            if (aIsLarger && !bIsLarger) return -1;
            if (!aIsLarger && bIsLarger) return 1;
            
            // If both are larger or both are smaller, prefer the larger one
            if (aIsLarger && bIsLarger) {
              return b.width - a.width; // Descending order (largest first)
            } else {
              return b.width - a.width; // Descending order (largest first)
            }
          });
          
          return sortedOneByOne[0]?.url || sortedOneByOne[0]?.link;
        }
      }
      
      // Fallback: Find the best aspect ratio match, then optimize by size
      const aspectRatioSorted = [...imageList].sort((a, b) => {
        const aAspectRatio = a.width / a.height;
        const bAspectRatio = b.width / b.height;
        const aDiff = Math.abs(containerProportion - aAspectRatio);
        const bDiff = Math.abs(containerProportion - bAspectRatio);
        
        return aDiff - bDiff; // Ascending order (best match first)
      });
      
      // Get all images that have similar aspect ratio to the best match
      const bestAspectRatio = aspectRatioSorted[0].width / aspectRatioSorted[0].height;
      const aspectRatioThreshold = 0.1; // Tight threshold for precise matching
      const similarAspectImages = imageList.filter(item => {
        const itemAspect = item.width / item.height;
        return Math.abs(itemAspect - bestAspectRatio) <= aspectRatioThreshold;
      });
      
      // Among similar aspect ratio images, sort by size, prioritizing larger than container
      const sizeSorted = similarAspectImages.sort((a, b) => {
        // First priority: images larger than container width
        const aIsLarger = a.width >= width;
        const bIsLarger = b.width >= width;
        
        if (aIsLarger && !bIsLarger) return -1;
        if (!aIsLarger && bIsLarger) return 1;
        
        // If both are larger or both are smaller, prefer the larger one
        if (aIsLarger && bIsLarger) {
          return b.width - a.width; // Descending order (largest first)
        } else {
          return b.width - a.width; // Descending order (largest first)
        }
      });
      
      return sizeSorted[0]?.url || sizeSorted[0]?.link;
    }

    return null;
  }, [imageList, src, imageRef]);

  const imageContainerClassList = useMemo(() => {
    const list = ['image__container'];

    if (withSkeleton) {
      list.push('image__container--with-skeleton');
    }

    if(imageContainerClassName) {
      list.push(imageContainerClassName);
    }

    return list.join(' ');
  }, [withSkeleton, imageContainerClassName]);

  return (
    <Lazy className={imageContainerClassList}>
      {!isLoaded && withSkeleton ?  <ImageSkeleton /> : null}

       <img
        data-testid="image"
        className={classList}
        style={style}
        src={source || undefined}
        alt={alt}
        ref={(refData) => {
          if (refData) {
            if (setRef) {
              setRef(refData);
            }
            setImageRef(refData);
          }
        }}
        onLoad={onLoad}
        {...props}
      />
    </Lazy>
  );
};

export default Image;
